import { useState } from 'react';
import { useDeleteInitialQuestionnaire } from '@/features/questionnaires/api';
import { StudyFormValues } from '@/features/studies';
import { StudyEditFormProps } from '@/features/studies';
import { Box, Button, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { ConfirmationDialog } from '@/shared/components/ui';
import { ControllerRenderProps } from 'react-hook-form';
import { useStudyFormContext } from '../contexts';

type Props = {
  field: ControllerRenderProps<StudyFormValues, 'initialQuestionnaire'>;
} & Pick<StudyEditFormProps, 'formLoading' | 'studyId'>;

export const InitialQuestionnaireSection = ({
  formLoading,
  studyId,
  field,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Paper
      sx={{
        p: 2,
        border: '1px dashed grey',
        borderRadius: 1,
        position: 'relative',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <AssignmentIcon sx={{ mr: 1, color: 'text.secondary' }} />
        <Typography variant="subtitle1" fontWeight="medium">
          {t('pages.studies.sections.initialQuestionnaire')}
        </Typography>
      </Box>
      <InitialQuestionnaireContent
        formLoading={formLoading}
        studyId={studyId}
        field={field}
      />
    </Paper>
  );
};

const InitialQuestionnaireContent = ({
  formLoading,
  studyId,
  field,
}: Props) => {
  const { t } = useTranslation();
  const { saveAndNavigate } = useStudyFormContext();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const deleteInitialQuestionnaire = useDeleteInitialQuestionnaire();
  const initialQuestionnaire = field.value;
  const id = initialQuestionnaire?.id;

  const handleEditInitialQuestionnaire = () => {
    saveAndNavigate(`/studies/${studyId}/initial-questionnaire/edit/${id}`);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (id) {
      deleteInitialQuestionnaire.mutate(id, {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          field.onChange(null);
        },
        onError: () => {
          setDeleteDialogOpen(false);
        },
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  if (!id || !initialQuestionnaire) {
    return (
      <EmptyInitialQuestionnaire formLoading={formLoading} studyId={studyId} />
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('pages.studies.initialQuestionnaire.edit.fields.title', {
          title: initialQuestionnaire.title,
        })}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleEditInitialQuestionnaire}
          disabled={formLoading}
        >
          {t('buttons.edit')}
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="error"
          onClick={handleDeleteClick}
          disabled={formLoading}
        >
          {t('buttons.delete')}
        </Button>
      </Box>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('pages.studies.initialQuestionnaire.delete.confirmTitle')}
        message={t('pages.studies.initialQuestionnaire.delete.confirmMessage')}
        confirmText={t('buttons.yes')}
        cancelText={t('buttons.no')}
        loading={deleteInitialQuestionnaire.isLoading}
      />
    </Box>
  );
};

const EmptyInitialQuestionnaire = ({
  formLoading,
  studyId,
}: Pick<StudyEditFormProps, 'formLoading' | 'studyId'>) => {
  const { t } = useTranslation();
  const { saveAndNavigate } = useStudyFormContext();

  const handleNavigateToCreateIQ = () => {
    saveAndNavigate(`/studies/${studyId}/initial-questionnaire/create`);
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('pages.studies.initialQuestionnaire.empty')}
      </Typography>
      <Button
        variant="contained"
        size="small"
        onClick={handleNavigateToCreateIQ}
        disabled={formLoading}
      >
        {t('buttons.createInitialQuestionnaire')}
      </Button>
    </Box>
  );
};
